/**
 * Overview Tab Component - Original dashboard content
 * Shows kill switch, costs, tokens, oracle usage, user search, AI quality, and logs
 */
import { 
    Zap, ZapOff, DollarSign, Cpu, BarChart3, Activity, 
    Users, Search, Clock, CheckCircle, XCircle, Loader2
} from 'lucide-react';
import { BentoCard, StatDisplay, NeonIcon, API_URL } from './shared';
import AIQualityMonitor from './AIQualityMonitor';

const OverviewTab = ({ 
    stats, logs, aiResponses, searchEmail, setSearchEmail, 
    searchResults, searchLoading, handleSearchUsers,
    handleToggleKillSwitch, killSwitchLoading, handleRateResponse, isSpanish 
}) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Kill Switch - Priority Card */}
        <BentoCard highlight={stats?.kill_switch_active} className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    {stats?.kill_switch_active ? (
                        <NeonIcon icon={ZapOff} neonColor="red" size="w-8 h-8" />
                    ) : (
                        <NeonIcon icon={Zap} neonColor="green" size="w-8 h-8" />
                    )}
                    <div>
                        <h3 className="font-bold text-white">AI Kill Switch</h3>
                        <p className="text-xs text-white/50">
                            {stats?.kill_switch_active ? 'AI DISABLED' : 'AI Enabled'}
                        </p>
                    </div>
                </div>
                <button
                    onClick={handleToggleKillSwitch}
                    disabled={killSwitchLoading}
                    data-testid="kill-switch-btn"
                    className={`
                        px-4 py-2 rounded-xl font-medium transition-all
                        ${stats?.kill_switch_active 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                        }
                    `}
                >
                    {killSwitchLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : stats?.kill_switch_active ? (
                        'Enable AI'
                    ) : (
                        'Disable AI'
                    )}
                </button>
            </div>
            {stats?.kill_switch_reason && (
                <p className="text-xs text-white/40 mt-2">
                    Reason: {stats.kill_switch_reason}
                </p>
            )}
        </BentoCard>
        
        {/* Today's Cost */}
        <BentoCard>
            <StatDisplay
                label="Today's Cost"
                value={`$${stats?.today_cost?.toFixed(4) || '0.0000'}`}
                icon={DollarSign}
                neonColor="green"
            />
        </BentoCard>
        
        {/* Today's Tokens */}
        <BentoCard>
            <StatDisplay
                label="Today's Tokens"
                value={stats?.today_tokens?.toLocaleString() || '0'}
                icon={Cpu}
                neonColor="cyan"
            />
        </BentoCard>
        
        {/* 7-Day Stats */}
        <BentoCard>
            <StatDisplay
                label="7-Day Cost"
                value={`$${stats?.period_cost?.toFixed(4) || '0.0000'}`}
                icon={BarChart3}
                neonColor="purple"
            />
        </BentoCard>
        
        {/* Total Requests */}
        <BentoCard>
            <StatDisplay
                label="7-Day Requests"
                value={stats?.period_requests?.toLocaleString() || '0'}
                icon={Activity}
                neonColor="yellow"
            />
        </BentoCard>
        
        {/* Usage by Oracle */}
        <BentoCard className="lg:col-span-2">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <NeonIcon icon={BarChart3} neonColor="magenta" size="w-4 h-4" />
                Usage by Oracle
            </h3>
            <div className="space-y-2">
                {(stats?.by_oracle || []).map((oracle, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-white/70 capitalize">{oracle.oracle}</span>
                        <div className="flex items-center gap-4">
                            <span className="text-white/50">{oracle.requests} requests</span>
                            <span className="text-green-400">${oracle.cost?.toFixed(4)}</span>
                        </div>
                    </div>
                ))}
                {(!stats?.by_oracle || stats.by_oracle.length === 0) && (
                    <p className="text-white/40 text-sm">No usage data yet</p>
                )}
            </div>
        </BentoCard>
        
        {/* User Search */}
        <BentoCard className="lg:col-span-2">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <NeonIcon icon={Users} neonColor="blue" size="w-4 h-4" />
                {isSpanish ? 'Soporte Astral - Buscar Usuario' : 'Astral Support - User Search'}
            </h3>
            <div className="flex gap-2 mb-3">
                <input
                    type="text"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder={isSpanish ? "Buscar por email..." : "Search by email..."}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchUsers()}
                    data-testid="user-search-input"
                />
                <button
                    onClick={handleSearchUsers}
                    disabled={searchLoading}
                    className="px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
                    data-testid="user-search-btn"
                >
                    {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
                {searchResults.map((user, i) => (
                    <div key={i} className="p-2 bg-white/5 rounded-lg text-sm">
                        <div className="flex justify-between">
                            <span className="text-white font-medium">{user.name || 'No name'}</span>
                            {user.is_admin && <span className="text-primary text-xs">ADMIN</span>}
                        </div>
                        <p className="text-white/50 text-xs">{user.email}</p>
                        <p className="text-white/40 text-xs">Born: {user.birth_date || 'Not set'}</p>
                    </div>
                ))}
            </div>
        </BentoCard>
        
        {/* AI Quality Monitor - Enhanced */}
        <AIQualityMonitor isSpanish={isSpanish} handleRateResponse={handleRateResponse} />
        
        {/* Recent Logs */}
        <BentoCard className="lg:col-span-4">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                {isSpanish ? 'Registro de Uso de Tokens' : 'Token Usage Logs'}
            </h3>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-white/50 text-xs uppercase">
                            <th className="text-left py-2">{isSpanish ? 'Hora' : 'Time'}</th>
                            <th className="text-left py-2">Endpoint</th>
                            <th className="text-left py-2">Oracle</th>
                            <th className="text-right py-2">Tokens</th>
                            <th className="text-right py-2">{isSpanish ? 'Costo' : 'Cost'}</th>
                            <th className="text-center py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.slice(0, 15).map((log, i) => (
                            <tr key={i} className="border-t border-white/5">
                                <td className="py-2 text-white/40">{log.timestamp?.split('T')[1]?.slice(0, 8) || '-'}</td>
                                <td className="py-2 text-white/70">{log.endpoint}</td>
                                <td className="py-2 text-primary capitalize">{log.oracle_type || '-'}</td>
                                <td className="py-2 text-right text-white/70">{log.total_tokens?.toLocaleString()}</td>
                                <td className="py-2 text-right text-green-400">${log.cost_estimate_usd?.toFixed(6)}</td>
                                <td className="py-2 text-center">
                                    {log.success ? (
                                        <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                                    ) : (
                                        <XCircle className="w-4 h-4 text-red-500 mx-auto" />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {logs.length === 0 && (
                    <p className="text-white/40 text-sm text-center py-4">
                        {isSpanish ? 'No hay registros de uso de tokens aún' : 'No token usage logs yet'}
                    </p>
                )}
            </div>
        </BentoCard>
    </div>
);

export default OverviewTab;
